/**
 * ConfigureContributionWrapper
 *
 * A wrapper that handles all data fetching and state management for the
 * EditContributionModalV2. This allows MiBeneficioV2 to open the
 * contribution modal without duplicating all the logic from ConfigurarBeneficio.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { analytics } from '../../services/analytics';
import { ArcaContext } from '../../context/ArcaContext.context';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import EditContributionModalV2 from './EditContributionModalV2';
import { ContributionTypeEnum } from '../../api/users/interfaces/ContributionData';
import { CompanyWithEppConfigI } from '../../api/companies/interfaces/CompanyInfo';
import EMPTY_CURRENT_USER, { CurrentUserI } from '../../api/users/interfaces/Users';
import getCompanyInfoEppConfig from '../../api/companies/getCompanyInfoEppConfig';
import { AdhesionMode } from '../../api/users/getAdhesionDetails';
import getCurrentUser from '../../api/users/getCurrentUser';
import getUserEppConfigForMode from '../../api/users/getUserEppConfigForMode';
import { UserEppConfigForModeDataI } from '../../api/users/interfaces/UserEppConfigForMode';
import updatePeriodicalContribution from '../../api/users/updatePeriodicalContribution';
import { resolveEmployerContribution } from '../../utils/resolveEmployerContribution';
import { usesReducedAutonomoCap } from '../../utils/contributionLimits';
import { useToast } from '../ui/use-toast';

export interface ConfigureContributionWrapperProps {
	isOpen: boolean;
	onClose: () => void;
	isIndividual?: boolean;
	onSaved?: () => void;
	/** Euros que suma el enlace del correo (Arca-Team/product#3). Ver `EditContributionModalV2`. */
	presetDeltaAmount?: number;
}

const ConfigureContributionWrapper: React.FC<ConfigureContributionWrapperProps> = ({
	isOpen,
	onClose,
	isIndividual = false,
	onSaved,
	presetDeltaAmount
}) => {
	const { token, companyId } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { toast } = useToast();

	// Company config
	const [companyEppInfo, setCompanyEppInfo] = useState<CompanyWithEppConfigI | null>(null);

	// User data
	const [currentUser, setCurrentUser] = useState<CurrentUserI>(EMPTY_CURRENT_USER);
	// Config (amount/tipo/día) del plan de ESTE modal, resuelta por modo — sustituye a
	// `currentUser.userEppConfig`, que resuelve "el plan por defecto" y puede no ser el
	// plan que este modal edita en multi-cuenta (Fase 4, épica multi-cuenta).
	const [eppConfigData, setEppConfigData] = useState<UserEppConfigForModeDataI>({});
	const mode: AdhesionMode = isIndividual ? 'INDIVIDUAL' : 'EMPLOYMENT';

	// Contribution state
	const [contributionType, setContributionType] = useState<ContributionTypeEnum>(
		ContributionTypeEnum.FIXED
	);
	const [contributionFixedAmount, setContributionFixedAmount] = useState<number | '' | undefined>(
		0
	);
	const [contributeAlwaysMax, setContributeAlwaysMax] = useState<boolean>(false);

	// Loading state
	const [loading, setLoading] = useState(true);

	// Fetch data when modal opens
	useEffect(() => {
		if (!isOpen || !token) return;

		const fetchData = async () => {
			setLoading(true);
			try {
				// Fetch company config
				if (companyId) {
					const companyRes = await getCompanyInfoEppConfig(token, companyId);
					const company =
						companyRes.data?.allCompanies?.find((c: CompanyWithEppConfigI) => c.id === companyId) ||
						companyRes.data?.allCompanies?.[0];
					if (company) {
						setCompanyEppInfo(company);
					}
				}

				// Fetch user data
				const userRes = await getCurrentUser(token);
				const userData = userRes.data as CurrentUserI;
				setCurrentUser(userData);

				// Config de ESTE plan por modo (amount/tipo/día) — no la config "por
				// defecto" de current-user, que en multi-cuenta puede ser otro plan.
				const eppConfigRes = await getUserEppConfigForMode(token, mode).catch(() => null);
				const eppConfig = eppConfigRes?.data?.data ?? {};
				setEppConfigData(eppConfig);

				// Set contribution values from user config
				setContributionFixedAmount(eppConfig.pensionContribution ?? 0);
				setContributeAlwaysMax(eppConfig.contributeAlwaysMax ?? false);
			} catch (error) {
				console.error('Error fetching contribution data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [isOpen, token, companyId, mode]);

	const handleChangeFixedContribution = useCallback((value: number) => {
		setContributionFixedAmount(value);
	}, []);

	// Devuelve si se guardó: el modal solo registra `contribution_updated` si fue bien.
	const handleSaveChangesEditFixedContribution = useCallback(async (): Promise<boolean> => {
		try {
			// Ensure we have a boolean value
			const booleanContributeAlwaysMax = contributeAlwaysMax === true;

			await updatePeriodicalContribution(
				token,
				Number(contributionFixedAmount),
				booleanContributeAlwaysMax,
				mode
			);

			analytics.contributionConfigured({
				amount: Number(contributionFixedAmount),
				contributeAlwaysMax: booleanContributeAlwaysMax
			});

			toast({
				title: translate('benefit.simulator.savedTitle'),
				description: translate('benefit.simulator.savedDescription')
			});

			onClose();
			onSaved?.();
			return true;
		} catch (error) {
			console.error('Error saving contribution:', error);
			toast({
				variant: 'destructive',
				title: translate('benefit.simulator.saveErrorTitle'),
				description: translate('benefit.simulator.saveErrorDescription')
			});
			return false;
		}
	}, [
		token,
		contributionFixedAmount,
		contributeAlwaysMax,
		onClose,
		onSaved,
		toast,
		translate,
		mode
	]);

	// Don't render modal until data is loaded
	if (!isOpen) return null;

	return (
		<EditContributionModalV2
			isOpen={isOpen}
			onClose={onClose}
			usesReducedCap={usesReducedAutonomoCap(currentUser?.user)}
			isIndividual={isIndividual}
			contributionType={contributionType}
			setContributionType={setContributionType}
			contributionFixedAmount={contributionFixedAmount}
			handleChangeFixedContribution={handleChangeFixedContribution}
			handleSaveChangesEditFixedContribution={handleSaveChangesEditFixedContribution}
			translate={translate}
			companyEppConfig={companyEppInfo?.companyEppConfig}
			contributeAlwaysMax={contributeAlwaysMax}
			setContributeAlwaysMax={setContributeAlwaysMax}
			userGrossAnnualSalary={currentUser?.user?.grossAnnualSalary}
			employeeContributionType={eppConfigData.employeeContributionType}
			employeeCategory={currentUser?.user?.employeeCategory || eppConfigData.employeeCategory}
			resolvedMonthlyCompanyContribution={resolveEmployerContribution({
				calculatedEmployerContribution: currentUser?.calculatedEmployerContribution,
				companyEppConfig: companyEppInfo?.companyEppConfig,
				employeeCategory: currentUser?.user?.employeeCategory || eppConfigData.employeeCategory,
				grossAnnualSalary: currentUser?.user?.grossAnnualSalary,
				employmentStartDate: currentUser?.user?.employmentStartDate
			})}
			// El preset del correo se entrega cuando esta config ya ha llegado: así su importe no
			// lo pisa una respuesta tardía, y el modal ya sabe si la empresa aporta por porcentaje.
			presetDeltaAmount={loading ? undefined : presetDeltaAmount}
			// Que viene del correo se sabe ya: el modal registra su apertura antes de que cargue.
			openedFromEmail={Boolean(presetDeltaAmount)}
		/>
	);
};

export default ConfigureContributionWrapper;
